<template>
  <div
    v-if="hasPartnerWorkflows"
    ref="partnerSelectionRoot"
    class="partner-selection"
  >
    <div class="primary-grey--text text-body-2 font-weight-bold mb-2">
      Trusted Partner Spotlight
    </div>
    <div class="primary-grey--text text-body-2 mb-4">
      Based on your question, we thought you might be interested to learn more about this partner
    </div>

    <GCarousel class="ma-2">
      <GCarouselItem
        v-for="partner in enrichedPartnerWorkflows"
        :key="partner.id"
      >
        <v-card
          class="partner-card d-flex flex-column"
          outlined
        >
          <v-card-text class="pa-4 d-flex flex-column flex-grow-1">
            <div class="d-flex align-start mb-3">
              <img
                v-if="partner.logo_url"
                :src="partner.logo_url"
                :alt="partner.name"
                class="partner-logo mr-3"
              >
              <v-icon
                v-else
                size="32"
                color="grey"
              >
                {{ isAgAdvisor(partner) ? 'mdi-account-tie' : 'mdi-domain' }}
              </v-icon>
              <div class="partner-header-text">
                <div class="text-subtitle-1 font-weight-bold primary-grey--text">
                  {{ partner.name }}
                </div>
                <div
                  v-if="getDescriptor(partner)"
                  class="text-caption grey--text"
                >
                  {{ getDescriptor(partner) }}
                </div>
              </div>
            </div>

            <div
              v-if="getHeadline(partner)"
              class="text-body-2 font-weight-medium primary-grey--text mb-2 partner-headline"
            >
              {{ getHeadline(partner) }}
            </div>

            <div
              v-if="getBody(partner)"
              class="text-body-2 primary-grey--text partner-body"
            >
              {{ getBody(partner) }}
            </div>
          </v-card-text>

          <v-card-actions class="px-4 pb-4 pt-0">
            <v-btn
              v-if="isAgAdvisor(partner)"
              :color="COLORS.grainfoxAIButton"
              class="white--text partner-cta-btn"
              small
              block
              :loading="initiatingChat === partner.id"
              :disabled="initiatingChat !== null"
              @click="handleStartAgAdvisorChat(partner)"
            >
              <v-icon
                small
                left
              >
                mdi-chat-outline
              </v-icon>
              Start a chat
            </v-btn>

            <template v-else-if="partner.filteredCtas.length > 0">
              <v-btn
                v-for="(cta, ctaIndex) in partner.filteredCtas"
                :key="cta.id"
                :outlined="isOutlinedButton(ctaIndex, partner.filteredCtas)"
                :color="COLORS.grainfoxAIButton"
                :class="{ 'white--text': !isOutlinedButton(ctaIndex, partner.filteredCtas) }"
                small
                class="partner-cta-btn"
                style="flex: 1 1 0; min-width: 0;"
                @click="handleCtaClick(cta)"
              >
                <v-icon
                  v-if="getCtaIcon(cta.action_type)"
                  small
                  left
                >
                  {{ getCtaIcon(cta.action_type) }}
                </v-icon>
                {{ cta.name }}
              </v-btn>
            </template>
          </v-card-actions>
        </v-card>
      </GCarouselItem>
    </GCarousel>
    <v-dialog
      v-model="showMessageDialog"
      max-width="480"
    >
      <v-card>
        <v-card-title class="text-h6">
          Start chat with {{ selectedPartnerName }}
        </v-card-title>
        <v-card-text>
          <p class="text-body-2 grey--text text--darken-1 mb-4">
            Send a message to start a conversation. This will open in the Messenger.
          </p>
          <v-textarea
            v-model="agadvisorMessage"
            outlined
            dense
            rows="3"
            placeholder="Write your message..."
            :color="COLORS.grainfoxAIButton"
            hide-details
            autofocus
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="closeMessageDialog"
          >
            Cancel
          </v-btn>
          <v-btn
            :color="COLORS.grainfoxAIButton"
            class="white--text"
            :disabled="!agadvisorMessage.trim()"
            :loading="initiatingChat !== null"
            @click="confirmStartChat"
          >
            <v-icon
              small
              left
            >
              mdi-send
            </v-icon>
            Send Message
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { COLORS } from '../../helpers/colors';
import GCarousel from '../generic/GCarousel.vue';
import GCarouselItem from '../generic/GCarouselItem.vue';

const ACTION_TYPE_ICONS = {
  link: 'mdi-open-in-new',
  phone: 'mdi-phone',
  email: 'mdi-email',
};

const PARTNER_TYPE_INDIVIDUAL = 'individual';

export default {
  name: 'PartnerSelection',

  components: {
    GCarousel,
    GCarouselItem,
  },

  data() {
    return {
      COLORS,
      showMessageDialog: false,
      agadvisorMessage: '',
      selectedPartner: null,
      initiatingChat: null,
    };
  },

  computed: {
    ...mapState('chatbot', ['partnerWorkflows']),
    ...mapGetters('chatbot', ['hasPartnerWorkflows', 'isStreaming']),

    selectedPartnerName() {
      return this.selectedPartner?.name || 'AgAdvisor';
    },

    enrichedPartnerWorkflows() {
      return this.partnerWorkflows.map((partner) => ({
        ...partner,
        filteredCtas: this.getFilteredCtas(partner),
      }));
    },
  },

  watch: {
    hasPartnerWorkflows(newVal) {
      if (newVal && !this.isStreaming) {
        this.emitScrollToPartners();
      }
    },

    isStreaming(newVal) {
      if (!newVal && this.hasPartnerWorkflows) {
        this.emitScrollToPartners();
      }
    },
  },

  methods: {
    ...mapActions('chatbot', ['trackCtaClick', 'initiateAgAdvisorChat']),

    isAgAdvisor(partner) {
      return partner?.partner_type === PARTNER_TYPE_INDIVIDUAL;
    },

    getDescriptor(partner) {
      return partner?.description?.descriptor || partner?.descriptor || null;
    },

    getHeadline(partner) {
      return partner?.description?.headline || partner?.headline || null;
    },

    getBody(partner) {
      return partner?.description?.body || partner?.body || null;
    },

    getCtaIcon(actionType) {
      return ACTION_TYPE_ICONS[actionType] || null;
    },

    getFilteredCtas(partner) {
      if (!partner?.ctas) return [];
      return partner.ctas.filter((cta) => {
        const name = (cta.name || '').toLowerCase();
        return !name.includes('learn more');
      });
    },

    handleCtaClick(cta) {
      if (cta?.id) {
        this.trackCtaClick(cta.id);
      }

      const actionValue = cta?.action_value;
      if (!actionValue) return;

      const actionType = cta?.action_type || 'link';

      switch (actionType) {
        case 'phone':
          window.open(`tel:${actionValue.replace(/\s/g, '')}`, '_self');
          break;
        case 'email':
          window.open(`mailto:${actionValue}`, '_self');
          break;
        case 'link':
        default:
          window.open(actionValue, '_blank', 'noopener,noreferrer');
          break;
      }
    },

    handleStartAgAdvisorChat(partner) {
      if (!partner?.partner_metadata?.account_id) {
        this.$snackbar?.error('Unable to start chat: advisor account not configured.');
        return;
      }
      this.selectedPartner = partner;
      this.agadvisorMessage = '';
      this.showMessageDialog = true;
    },

    closeMessageDialog() {
      this.showMessageDialog = false;
      this.selectedPartner = null;
      this.agadvisorMessage = '';
    },

    async confirmStartChat() {
      if (!this.agadvisorMessage.trim() || !this.selectedPartner) return;

      const partner = this.selectedPartner;
      this.initiatingChat = partner.id;

      try {
        const result = await this.initiateAgAdvisorChat({
          agadvisorAccountId: partner.partner_metadata?.account_id,
          message: this.agadvisorMessage.trim(),
          sinoaPartnerId: partner.id,
        });

        if (result?.ok && result?.conversation_id) {
          this.closeMessageDialog();
          window.location.href = `/messenger/${result.conversation_id}/`;
        } else {
          this.$snackbar?.error(result?.error || 'Failed to start chat');
        }
      } catch (error) {
        this.$snackbar?.error('Failed to start chat. Please try again.');
      } finally {
        this.initiatingChat = null;
      }
    },

    isOutlinedButton(index, ctas) {
      if (ctas.length <= 1) return false;
      const cta = ctas[index];
      if (cta.is_primary !== undefined) return !cta.is_primary;
      return index > 0;
    },

    emitScrollToPartners() {
      this.$nextTick(() => {
        if (this.$refs.partnerSelectionRoot) {
          this.$emit('scroll-to-partners', this.$refs.partnerSelectionRoot);
        }
      });
    },
  },
};
</script>

<style scoped lang="scss">
.partner-logo {
  height: 40px;
  width: auto;
  max-width: 150px;
  object-fit: contain;
  display: block;
}

.partner-selection {
  margin-top: 16px;

  ::v-deep .g-carousel__items {
    justify-content: flex-start;
  }
}

.partner-card {
  border: 1px solid #12A8C4 !important;
  background-color: #E9F6FD !important;
  border-radius: 8px !important;
  width: 320px;
  min-width: 280px;
  max-width: 360px;
}

.partner-header-text {
  flex: 1;
  min-width: 0;
}

.partner-headline {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
}

.partner-body {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.5;
}

.partner-cta-btn {
  text-transform: none;
  font-weight: 500;
  letter-spacing: 0;
  padding: 0 12px !important;
}

.partner-cta-btn .v-icon {
  margin-right: 6px;
}

.partner-cta-btn.v-btn--outlined:hover {
  background-color: rgba(18, 168, 196, 0.08) !important;
}

.partner-cta-btn:not(.v-btn--outlined):hover {
  background-color: #109bb5 !important;
}

@media (max-width: 600px) {
  ::v-deep .g-carousel {
    max-width: 100vw;
    overflow: hidden;
  }

  .partner-card {
    width: 260px !important;
    min-width: 260px !important;
    max-width: 260px !important;
  }

  ::v-deep .g-carousel__items {
    max-width: calc(100vw - 48px);
  }
}
</style>
